import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  alpha,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import {
  TableChart as TableChartIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

interface ValuationMethod {
  name: string;
  value: number;
  confidence: 'high' | 'medium' | 'low';
  details: any;
  metadata?: {
    name: string;
    description: string;
    formula: string;
    bestFor: string[];
    limitations: string[];
    parameters: Record<string, any>;
  };
}

interface ValuationSummary {
  weightedValue: number;
  aiEnhancedValue: number;
  valueRange: { low: number; high: number };
  confidence: string;
  keyDrivers: string[];
  recommendations: string[];
}

interface ValuationResultsPanelProps {
  methods: Record<string, ValuationMethod>;
  summary?: ValuationSummary;
}

const ValuationResultsPanel: React.FC<ValuationResultsPanelProps> = ({
  methods,
  summary
}) => {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState<'chart' | 'table' | 'comparison'>('chart');

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: value >= 1000000 ? 'compact' : 'standard'
    }).format(value);
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence.toLowerCase()) {
      case 'high': return theme.palette.success.main;
      case 'medium': return theme.palette.warning.main;
      case 'low': return theme.palette.error.main;
      default: return theme.palette.text.secondary;
    }
  };

  const getConfidenceIcon = (confidence: string) => {
    switch (confidence.toLowerCase()) {
      case 'high': return <CheckCircleIcon />;
      case 'medium': return <WarningIcon />;
      case 'low': return <ErrorIcon />;
      default: return <WarningIcon />;
    }
  };

  // Prepare chart data
  const chartData = Object.entries(methods)
    .filter(([key, method]) => !method.name.includes("Weighted") && !method.name.includes("AI Enhanced"))
    .map(([key, method]) => ({
      name: method.name.replace(' Method', ''),
      value: method.value,
      confidence: method.confidence,
      color: getConfidenceColor(method.confidence)
    }))
    .sort((a, b) => b.value - a.value);

  const pieData = chartData.map((item, index) => ({
    ...item,
    percentage: ((item.value / chartData.reduce((sum, d) => sum + d.value, 0)) * 100).toFixed(1)
  }));

  const COLORS = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main
  ];

  // Calculate statistics
  const values = chartData.map(d => d.value);
  const stats = {
    highest: Math.max(...values),
    lowest: Math.min(...values),
    average: values.reduce((sum, val) => sum + val, 0) / values.length,
    variance: ((Math.max(...values) - Math.min(...values)) / Math.max(...values) * 100).toFixed(1)
  };

  const renderChartView = () => (
    <Grid container spacing={3}>
      {/* Bar Chart */}
      <Grid item xs={12} lg={8}>
        <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Valuation Methods Comparison
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis
                  dataKey="name"
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  tickFormatter={(value) => value >= 1000000 ? `$${(value/1000000).toFixed(1)}M` : `$${(value/1000).toFixed(0)}K`}
                />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    borderColor: theme.palette.divider,
                    borderRadius: 8
                  }}
                  formatter={(value: number) => [formatCurrency(value), 'Valuation']}
                />
                <Bar
                  dataKey="value"
                  radius={[4, 4, 0, 0]}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Pie Chart */}
      <Grid item xs={12} lg={4}>
        <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Value Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }) => `${name} ${percentage}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    borderColor: theme.palette.divider,
                    borderRadius: 8
                  }}
                  formatter={(value: number) => [formatCurrency(value), 'Valuation']}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Statistics Cards */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Highest Valuation
                </Typography>
                <Typography variant="h5" fontWeight={700} color="success.main">
                  {formatCurrency(stats.highest)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Lowest Valuation
                </Typography>
                <Typography variant="h5" fontWeight={700} color="error.main">
                  {formatCurrency(stats.lowest)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Average Valuation
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {formatCurrency(stats.average)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Valuation Range
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  ±{stats.variance}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  const renderTableView = () => (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Method</TableCell>
            <TableCell align="right">Valuation</TableCell>
            <TableCell align="center">Confidence</TableCell>
            <TableCell>Key Parameters</TableCell>
            <TableCell>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(methods)
            .filter(([key, method]) => !method.name.includes("Weighted") && !method.name.includes("AI Enhanced"))
            .sort(([, a], [, b]) => b.value - a.value)
            .map(([key, method]) => (
              <TableRow key={key}>
                <TableCell component="th" scope="row">
                  <Typography variant="subtitle2" fontWeight={600}>
                    {method.name}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="h6" fontWeight={700} color={getConfidenceColor(method.confidence)}>
                    {formatCurrency(method.value)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    icon={getConfidenceIcon(method.confidence)}
                    label={method.confidence.toUpperCase()}
                    color={method.confidence === 'high' ? 'success' : method.confidence === 'medium' ? 'warning' : 'error'}
                    variant="outlined"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ maxWidth: 200 }}>
                    {method.details && Object.entries(method.details).slice(0, 3).map(([paramKey, paramValue]) => (
                      <Typography key={paramKey} variant="caption" display="block" color="text.secondary">
                        {paramKey}: {typeof paramValue === 'number' ?
                          (paramKey.includes('rate') || paramKey.includes('multiple') ?
                            (paramValue < 1 ? `${(paramValue * 100).toFixed(1)}%` : paramValue.toFixed(2)) :
                            paramValue.toLocaleString()) :
                          String(paramValue)
                        }
                      </Typography>
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {method.metadata?.description || 'Standard valuation method'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderComparisonView = () => (
    <Grid container spacing={3}>
      {/* Confidence Analysis */}
      <Grid item xs={12} md={6}>
        <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Confidence Analysis
            </Typography>
            <List>
              {['high', 'medium', 'low'].map((confidenceLevel) => {
                const methodsWithConfidence = Object.values(methods).filter(m =>
                  m.confidence === confidenceLevel &&
                  !m.name.includes("Weighted") &&
                  !m.name.includes("AI Enhanced")
                );
                const avgValue = methodsWithConfidence.length > 0
                  ? methodsWithConfidence.reduce((sum, m) => sum + m.value, 0) / methodsWithConfidence.length
                  : 0;

                return (
                  <ListItem key={confidenceLevel}>
                    <ListItemIcon>
                      {getConfidenceIcon(confidenceLevel)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle2">
                            {confidenceLevel.charAt(0).toUpperCase() + confidenceLevel.slice(1)} Confidence
                          </Typography>
                          <Typography variant="subtitle2" fontWeight={700}>
                            {formatCurrency(avgValue)}
                          </Typography>
                        </Box>
                      }
                      secondary={`${methodsWithConfidence.length} method(s) • Average valuation`}
                    />
                  </ListItem>
                );
              })}
            </List>
          </CardContent>
        </Card>
      </Grid>

      {/* Method Reliability */}
      <Grid item xs={12} md={6}>
        <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Method Reliability Ranking
            </Typography>
            <List>
              {Object.entries(methods)
                .filter(([key, method]) => !method.name.includes("Weighted") && !method.name.includes("AI Enhanced"))
                .sort(([, a], [, b]) => {
                  const confidenceScore = { high: 3, medium: 2, low: 1 };
                  return (confidenceScore[b.confidence] || 0) - (confidenceScore[a.confidence] || 0);
                })
                .map(([key, method], index) => (
                  <ListItem key={key}>
                    <ListItemIcon>
                      <Typography variant="h6" color="primary">
                        #{index + 1}
                      </Typography>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle2">
                            {method.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                              {formatCurrency(method.value)}
                            </Typography>
                            <Chip
                              label={method.confidence}
                              color={method.confidence === 'high' ? 'success' : method.confidence === 'medium' ? 'warning' : 'error'}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
            </List>
          </CardContent>
        </Card>
      </Grid>

      {/* Value Range Analysis */}
      <Grid item xs={12}>
        <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Valuation Range Analysis
            </Typography>
            <Box sx={{ p: 2, backgroundColor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Range Spread
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {formatCurrency(stats.highest - stats.lowest)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Coefficient of Variation
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {stats.variance}%
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Interpretation
                  </Typography>
                  <Typography variant="body2">
                    {parseFloat(stats.variance) < 15
                      ? "Low variance indicates strong consensus among methods."
                      : parseFloat(stats.variance) < 30
                      ? "Moderate variance suggests reasonable agreement with some differences."
                      : "High variance indicates significant disagreement among methods."
                    }
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  if (!methods || Object.keys(methods).length === 0) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom fontWeight={600}>
          Valuation Results
        </Typography>
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary">
              No valuation results available. Please generate a valuation first.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Valuation Results
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comprehensive analysis of all valuation methods and their results
          </Typography>
        </Box>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, newMode) => newMode && setViewMode(newMode)}
          size="small"
        >
          <ToggleButton value="chart" aria-label="chart view">
            <BarChartIcon sx={{ mr: 1 }} />
            Charts
          </ToggleButton>
          <ToggleButton value="table" aria-label="table view">
            <TableChartIcon sx={{ mr: 1 }} />
            Table
          </ToggleButton>
          <ToggleButton value="comparison" aria-label="comparison view">
            <PieChartIcon sx={{ mr: 1 }} />
            Analysis
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Special Methods Summary */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6}>
            <Card elevation={2} sx={{ border: `2px solid ${theme.palette.primary.main}` }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" color="primary" gutterBottom>
                  AI Enhanced Valuation
                </Typography>
                <Typography variant="h4" fontWeight={700} color="primary.main">
                  {formatCurrency(summary.aiEnhancedValue)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Machine learning enhanced result
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Card elevation={2} sx={{ border: `2px solid ${theme.palette.success.main}` }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" color="success.main" gutterBottom>
                  Weighted Average
                </Typography>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {formatCurrency(summary.weightedValue)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Confidence-weighted average
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Content based on view mode */}
      {viewMode === 'chart' && renderChartView()}
      {viewMode === 'table' && renderTableView()}
      {viewMode === 'comparison' && renderComparisonView()}
    </Box>
  );
};

export default ValuationResultsPanel;